import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Stage = {
  id: number
  pipeline: number
  name: string
  order: number
  color: string
  is_won: boolean
  is_lost: boolean
  application_count: number
}

type Pipeline = {
  id: number
  name: string
  description: string
  is_default: boolean
  is_active: boolean
  stages: Stage[]
  stage_count: number
  jobs_in_use: number
  has_won_stage: boolean
  has_lost_stage: boolean
}

type CreateInput = {
  name: string
  description: string
  is_default: boolean
}

export default function ATSPipelines() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const pipelinesQ = useQuery({
    queryKey: ['ats', 'pipelines'],
    queryFn: async () => (await api.get<Pipeline[]>('/ats/pipelines/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())

  function openCreate() {
    setDraft(emptyDraft())
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => {
      const created = (await api.post<Pipeline>('/ats/pipelines/', {
        name: draft.name,
        description: draft.description,
        is_active: true,
      })).data
      // Apply default flag through the dedicated action so the previous
      // default gets cleared atomically.
      if (draft.is_default) {
        await api.post(`/ats/pipelines/${created.id}/set-default/`)
      }
      return created
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['ats', 'pipelines'] })
      toast.success('Pipeline created — add stages on the next page')
      setCreating(false)
      navigate(`/admin/ats/pipelines/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/ats/pipelines/${id}/`),
    onSuccess: () => {
      toast.success('Pipeline deleted')
      qc.invalidateQueries({ queryKey: ['ats', 'pipelines'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const pipelines = pipelinesQ.data || []

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Hiring pipelines</h2>
          <p className="text-sm text-muted-foreground">
            Configure the stages every job's candidates flow through. New jobs default to the pipeline marked DEFAULT.
          </p>
        </div>
        <Button onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> New pipeline</Button>
      </div>

      {pipelinesQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : pipelines.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          No pipelines yet. Create one to start tracking candidates.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Name</Th>
                <Th>Description</Th>
                <Th className="text-right">Stages</Th>
                <Th className="text-right">Jobs in use</Th>
                <Th className="text-center">Default</Th>
                <Th className="text-center">Active</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {pipelines.map((p) => {
                const incomplete = !p.has_won_stage || !p.has_lost_stage
                return (
                  <tr
                    key={p.id}
                    className="hover:bg-muted/40 cursor-pointer"
                    onClick={() => navigate(`/admin/ats/pipelines/${p.id}`)}
                  >
                    <Td>
                      <div className="font-medium text-foreground">{p.name}</div>
                      {incomplete && (
                        <div className="text-xs text-amber-700 mt-0.5">
                          ⚠ Missing {!p.has_won_stage && 'won'}
                          {!p.has_won_stage && !p.has_lost_stage && ' + '}
                          {!p.has_lost_stage && 'lost'} stage
                        </div>
                      )}
                    </Td>
                    <Td className="text-xs">
                      {p.description || <span className="text-muted-foreground">—</span>}
                    </Td>
                    <Td className="text-right text-sm">{p.stage_count}</Td>
                    <Td className="text-right text-sm">
                      {p.jobs_in_use > 0
                        ? <span className="text-violet-700 font-semibold">{p.jobs_in_use}</span>
                        : <span className="text-muted-foreground">0</span>}
                    </Td>
                    <Td className="text-center">
                      {p.is_default && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold">
                          DEFAULT
                        </span>
                      )}
                    </Td>
                    <Td className="text-center">
                      <span className={`inline-block w-2 h-2 rounded-full ${p.is_active ? 'bg-green-500' : 'bg-muted-foreground'}`} />
                    </Td>
                    <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                            <MoreVertical className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/admin/ats/pipelines/${p.id}`)}>
                            Open
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={p.jobs_in_use > 0}
                            onClick={() => p.jobs_in_use === 0 && confirm(
                              `pipeline "${p.name}"`,
                              () => deleteMut.mutate(p.id),
                            )}
                            className={p.jobs_in_use === 0 ? 'text-destructive' : ''}
                          >
                            {p.jobs_in_use === 0
                              ? 'Delete pipeline'
                              : `Delete (in use by ${p.jobs_in_use} job${p.jobs_in_use === 1 ? '' : 's'})`}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New hiring pipeline"
        description="Create the pipeline. Stages are added on the pipeline detail page after creation."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="pl-name">Name *</FieldLabel>
            <Input id="pl-name" autoFocus required value={draft.name}
              onChange={(ev) => setDraft({ ...draft, name: ev.target.value })}
              placeholder="Engineering pipeline / Exec search / Default" />
          </Field>
          <Field>
            <FieldLabel htmlFor="pl-desc">Description</FieldLabel>
            <Textarea id="pl-desc" rows={3} value={draft.description}
              onChange={(ev) => setDraft({ ...draft, description: ev.target.value })}
              placeholder="When should this pipeline be used?" />
          </Field>
          <Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.is_default}
                onChange={(ev) => setDraft({ ...draft, is_default: ev.target.checked })} />
              Set as default pipeline
            </label>
            <FieldDescription>
              New jobs created without an explicit pipeline pick will use the default.
            </FieldDescription>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return { name: '', description: '', is_default: false }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
